import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, MapPin, Phone, Mail, Menu, X, ArrowRight, Clock, Users, Award, Star, Quote, Shield, Zap, Coffee, Car, Shirt, Calendar, ChevronRight, Info, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { SEO } from '../components/SEO';
import { TurfDatePicker } from '../components/TurfDatePicker';

import { API_URL } from '../utils/api';

const amenities = [
  { icon: Shield, title: 'FIFA-Quality Turf' },
  { icon: Zap, title: 'LED Floodlights' },
  { icon: Shirt, title: 'Changing Rooms' },
  { icon: Car, title: 'Free Parking' },
  { icon: Coffee, title: 'Beverages' },
  { icon: Award, title: '24/7 Available' },
];

const galleryImages = [
  { src: 'https://customer-assets.emergentagent.com/job_turf-manager-pro-1/artifacts/x0jllz09_2026-04-14.jpg', caption: 'Cricket Pitch Ready' },
  { src: 'https://customer-assets.emergentagent.com/job_turf-manager-pro-1/artifacts/ws9ntdum_IMG_6511.JPG', caption: 'Turf Under Open Sky' },
  { src: 'https://customer-assets.emergentagent.com/job_turf-manager-pro-1/artifacts/q69d6634_2026-04-14.webp', caption: 'Night Setup' },
  { src: 'https://customer-assets.emergentagent.com/job_turf-manager-pro-1/artifacts/axr7343v_2026-04-18%20%281%29.webp', caption: 'Night Match Under Lights' },
  { src: 'https://customer-assets.emergentagent.com/job_turf-manager-pro-1/artifacts/mz8ki7sm_2026-04-18.webp', caption: 'Team Cricket Night' },
];

const testimonials = [
  { name: 'Rahul Sharma', rating: 5, text: 'Best turf in Malda! The surface quality is top-notch and the lighting for night matches is fantastic. We play here every weekend.' },
  { name: 'Arjun Das', rating: 5, text: 'Booked a slot for our office cricket tournament. The booking process was seamless and the facilities exceeded our expectations.' },
  { name: 'Sneha Roy', rating: 4, text: 'Great place for football practice. Clean changing rooms, well-maintained turf, and the 24/7 availability is a huge plus for us.' },
  { name: 'Vikram Singh', rating: 5, text: 'We hosted a corporate event here and the team was incredibly supportive. The turf quality rivals any metro city facility.' },
  { name: 'Amit Ghosh', rating: 5, text: 'My go-to turf for weekend cricket. The happy hour pricing is unbeatable and the online booking saves so much time.' },
  { name: 'Priya Mondal', rating: 4, text: 'Excellent facilities for both cricket and football. My kids love playing here. Safe environment with good parking space.' },
];

function GallerySection({ images }) {
  return (
    <section id="gallery" className="py-20 md:py-24 bg-[#111111]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="text-center mb-10">
          <span className="text-[#16A34A] text-[11px] font-semibold tracking-[0.2em] uppercase font-heading">Gallery</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 font-heading">Happy Moments</h2>
        </div>

        {/* Desktop: Full-width masonry grid */}
        <div className="hidden md:grid grid-cols-12 gap-3" style={{ gridTemplateRows: '250px 250px' }}>
          <div className="col-span-5 row-span-2 group relative overflow-hidden rounded-2xl bg-[#0A0A0A]" data-testid="gallery-image-0">
            <img src={images[0].src} alt={images[0].caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
              <span className="text-[13px] font-medium font-body text-white">{images[0].caption}</span>
            </div>
          </div>
          <div className="col-span-4 group relative overflow-hidden rounded-2xl bg-[#0A0A0A]" data-testid="gallery-image-1">
            <img src={images[1].src} alt={images[1].caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <span className="text-[12px] font-medium font-body text-white">{images[1].caption}</span>
            </div>
          </div>
          <div className="col-span-3 row-span-2 group relative overflow-hidden rounded-2xl bg-[#0A0A0A]" data-testid="gallery-image-2">
            <img src={images[2].src} alt={images[2].caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <span className="text-[12px] font-medium font-body text-white">{images[2].caption}</span>
            </div>
          </div>
          <div className="col-span-2 group relative overflow-hidden rounded-2xl bg-[#0A0A0A]" data-testid="gallery-image-3">
            <img src={images[3].src} alt={images[3].caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <span className="text-[12px] font-medium font-body text-white">{images[3].caption}</span>
            </div>
          </div>
          <div className="col-span-2 group relative overflow-hidden rounded-2xl bg-[#0A0A0A]" data-testid="gallery-image-4">
            <img src={images[4].src} alt={images[4].caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <span className="text-[12px] font-medium font-body text-white">{images[4].caption}</span>
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden space-y-3">
          <div className="group relative overflow-hidden rounded-2xl aspect-[16/10] bg-[#0A0A0A]" data-testid="gallery-image-mobile-0">
            <img src={images[0].src} alt={images[0].caption} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-end p-4">
              <span className="text-[12px] font-medium font-body text-white">{images[0].caption}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] bg-[#0A0A0A]" data-testid="gallery-image-mobile-1">
              <img src={images[1].src} alt={images[1].caption} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-end p-3">
                <span className="text-[11px] font-medium font-body text-white">{images[1].caption}</span>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] bg-[#0A0A0A]" data-testid="gallery-image-mobile-2">
              <img src={images[2].src} alt={images[2].caption} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-end p-3">
                <span className="text-[11px] font-medium font-body text-white">{images[2].caption}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-[#0A0A0A]" data-testid="gallery-image-mobile-3">
              <img src={images[3].src} alt={images[3].caption} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-end p-3">
                <span className="text-[11px] font-medium font-body text-white">{images[3].caption}</span>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-[#0A0A0A]" data-testid="gallery-image-mobile-4">
              <img src={images[4].src} alt={images[4].caption} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-end p-3">
                <span className="text-[11px] font-medium font-body text-white">{images[4].caption}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSending, setContactSending] = useState(false);
  const [todaySlots, setTodaySlots] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSlotsLoading(true);
    axios.get(`${API_URL}/api/availability?date=${selectedDate}`)
      .then(r => { if (!cancelled) setTodaySlots(r.data); })
      .catch(() => { if (!cancelled) setTodaySlots(null); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDate]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await axios.post(`${API_URL}/api/contact`, contactForm);
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setContactSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
    } finally {
      setContactSending(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Premium Cricket & Football Turf | Book Online"
        description="Book hourly cricket and football turf slots online at Malda Turf. Premium grass, floodlights, instant confirmation, secure online payments. Open 24×7 in Malda, West Bengal."
        path="/"
      />
      {/* ─── Navbar ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="h-[72px] flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="w-9 h-9 bg-[#16A34A] flex items-center justify-center" style={{ borderRadius: '50px' }}>
                <Trophy className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
              </div>
              <div className="leading-tight">
                <span className="text-[15px] font-bold font-heading block">Malda Turf</span>
                <span className="text-[9px] text-neutral-500 uppercase tracking-[0.15em] font-body hidden sm:block">Premium Sports</span>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-1">
              {[{ label: 'Pricing', id: 'pricing' }, { label: 'Gallery', id: 'gallery' }, { label: 'Reviews', id: 'reviews' }, { label: 'Contact', id: 'contact' }].map(item => (
                <button key={item.id} onClick={() => scrollTo(item.id)} className="text-[13px] text-neutral-400 hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-all font-medium font-body" data-testid={`nav-${item.id}`}>
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/login')} data-testid="nav-get-started" className="bg-[#16A34A] hover:bg-[#15803D] text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl transition-colors font-heading flex items-center gap-1.5">
                Book Now <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button className="md:hidden text-neutral-400 hover:text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-[#0A0A0A] border-t border-[#1A1A1A] px-6 py-5 space-y-1">
            {['Pricing', 'Gallery', 'Reviews', 'Contact'].map(item => (
              <button key={item} onClick={() => scrollTo(item.toLowerCase())} className="block w-full text-left text-sm text-neutral-400 hover:text-white py-2.5 px-3 rounded-lg hover:bg-white/5 transition-all font-body">{item}</button>
            ))}
          </motion.div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/18717024/pexels-photo-18717024.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 w-full pt-28 pb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8" data-testid="hero-label">
              <div className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse"></div>
              <span className="text-[12px] text-neutral-300 font-medium font-body">Now Open 24/7 in Malda</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-[5.5rem] font-extrabold tracking-tight leading-[1.02] mb-6 font-heading">
              Your Game.<br /><span className="text-[#16A34A]">Your Turf.</span>
            </h1>
            <span className="sr-only">Premium cricket and football turf in Malda, West Bengal. Book hourly slots online with instant confirmation.</span>

            <p className="text-base md:text-lg text-neutral-300 leading-relaxed mb-10 max-w-lg font-body">
              Professional cricket & football turfs with hourly booking, instant confirmation, and world-class facilities.
            </p>

            <div className="flex flex-wrap gap-3 mb-16">
              <button onClick={() => navigate('/login')} data-testid="hero-book-now-button" className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold px-7 py-3.5 rounded-xl text-sm flex items-center gap-2 transition-colors font-heading">
                Book a Slot <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => scrollTo('pricing')} className="bg-white/10 backdrop-blur-sm hover:bg-white/15 text-white font-semibold px-7 py-3.5 rounded-xl text-sm transition-colors font-heading">
                View Pricing
              </button>
            </div>

            <div className="flex gap-8 md:gap-12">
              {[{ val: '100Hrs+', label: 'Booking' }, { val: '500+', label: 'Players' }, { val: '24/7', label: 'Open' }].map(s => (
                <div key={s.label}>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{s.val}</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-wider font-body">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Features Strip ─── */}
      <section className="bg-[#111111] border-y border-[#1A1A1A]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-[#1A1A1A]">
            {amenities.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} className="flex flex-col items-center justify-center py-5 md:py-7 gap-2" data-testid={`amenity-${i}`}>
                  <Icon className="h-[18px] w-[18px] text-[#16A34A]" />
                  <span className="text-[10px] md:text-[11px] text-neutral-500 font-medium text-center leading-tight font-body">{a.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Live Availability (Today or any future date) ─── */}
      {todaySlots && (
        <section className="py-16 md:py-20 px-6 md:px-10 lg:px-16 bg-[#111111]" data-testid="today-slots-section">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex flex-col gap-5 mb-8 md:flex-row md:items-end md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse"></div>
                  <span className="text-[11px] text-[#16A34A] font-semibold tracking-[0.15em] uppercase font-heading">Live Availability</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-heading">{todaySlots.is_today ? "Today's Slots" : 'Slot Availability'}</h2>
                <p className="text-sm text-neutral-500 mt-1.5 font-body">
                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {todaySlots.is_today && todaySlots.ist_time ? <> &middot; {todaySlots.ist_time}</> : null}
                  {todaySlots.is_weekend ? <span className="text-[#16A34A] font-medium"> &middot; Weekend rate</span> : null}
                </p>
              </div>
              <div className="flex flex-col gap-2.5 md:items-end md:max-w-[18rem] md:w-[18rem] flex-shrink-0">
                <div className="w-full">
                  <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5 font-body">Check another date</label>
                  <TurfDatePicker
                    value={selectedDate}
                    onChange={setSelectedDate}
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    showWeekendBadge
                    testId="landing-date-picker"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {/* Happy Hours Row */}
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 md:p-5" data-testid="availability-happy-hours">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-neutral-500" />
                    <span className="text-[13px] font-semibold font-heading">Happy Hours</span>
                    <span className="text-[11px] text-neutral-600 font-body">3 AM – 3 PM</span>
                  </div>
                  <span className="text-[12px] font-medium text-[#16A34A] font-body">{slotsLoading ? '...' : `${todaySlots.happy_hours.filter(s => s.available).length} open`}</span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
                  {todaySlots.happy_hours.map((slot, i) => {
                    const h = parseInt(slot.time);
                    const label = h === 0 ? '12AM' : h < 12 ? `${h}AM` : h === 12 ? '12PM' : `${h - 12}PM`;
                    return (
                      <div key={i} className={`py-2 rounded-lg text-center text-[11px] font-medium font-body transition-all ${slot.available ? 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 hover:bg-[#16A34A]/20 cursor-pointer' : 'bg-[#1A1A1A]/50 text-neutral-600 line-through'}`} data-testid={`happy-slot-${slot.time}`}>
                        {label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Rush Hours Row */}
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 md:p-5" data-testid="availability-rush-hours">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Zap className="h-3.5 w-3.5 text-neutral-500" />
                    <span className="text-[13px] font-semibold font-heading">Rush Hours</span>
                    <span className="text-[11px] text-neutral-600 font-body">3 PM – 3 AM</span>
                  </div>
                  <span className="text-[12px] font-medium text-[#16A34A] font-body">{slotsLoading ? '...' : `${todaySlots.rush_hours.filter(s => s.available).length} open`}</span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
                  {todaySlots.rush_hours.map((slot, i) => {
                    const h = parseInt(slot.time);
                    const label = h === 0 ? '12AM' : h < 12 ? `${h}AM` : h === 12 ? '12PM' : `${h - 12}PM`;
                    return (
                      <div key={i} className={`py-2 rounded-lg text-center text-[11px] font-medium font-body transition-all ${slot.available ? 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 hover:bg-[#16A34A]/20 cursor-pointer' : 'bg-[#1A1A1A]/50 text-neutral-600 line-through'}`} data-testid={`rush-slot-${slot.time}`}>
                        {label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        </section>
      )}

      {/* ─── Pricing ─── */}
      <section id="pricing" className="relative py-16 md:py-20 px-6 md:px-10 lg:px-16 overflow-hidden pb-6 md:pb-8">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#16A34A]/3 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-[1440px] mx-auto relative z-10">
          <div className="text-center mb-10">
            <span className="text-[#16A34A] text-[11px] font-semibold tracking-[0.2em] uppercase font-heading">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-3 font-heading">Transparent Rates</h2>
            <p className="text-sm text-neutral-500 mt-2 font-body">No hidden fees. Pay only for the hours you play.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-b from-[#141414] to-[#0E0E0E] rounded-2xl border border-[#1E1E1E] overflow-hidden" data-testid="pricing-card-merged">
              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#1A1A1A]">
                {/* Happy Hours */}
                <div className="p-6 md:p-8" data-testid="pricing-card-happy-hours">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                      <Clock className="h-4 w-4 text-[#16A34A]" />
                      <div>
                        <h3 className="text-[15px] font-bold font-heading">Happy Hours</h3>
                        <span className="text-[10px] text-neutral-500 font-body">3 AM — 3 PM</span>
                      </div>
                    </div>
                    <span className="text-[9px] text-[#16A34A]/70 bg-[#16A34A]/8 border border-[#16A34A]/15 px-2.5 py-0.5 rounded-full font-semibold font-body uppercase tracking-wider">Best Value</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-3.5 text-center" data-testid="weekday-price-happy">
                      <div className="text-[9px] text-neutral-600 uppercase tracking-wider mb-1 font-body">Mon — Fri</div>
                      <div className="flex items-baseline justify-center gap-0.5">
                        <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>₹300</span>
                        <span className="text-[10px] text-neutral-600 font-body">/hr</span>
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-3.5 text-center" data-testid="weekend-price-happy">
                      <div className="text-[9px] text-neutral-600 uppercase tracking-wider mb-1 font-body">Sat — Sun</div>
                      <div className="flex items-baseline justify-center gap-0.5">
                        <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>₹400</span>
                        <span className="text-[10px] text-neutral-600 font-body">/hr</span>
                      </div>
                    </div>
                  </div>

                  <button onClick={() => navigate('/login')} data-testid="book-happy-hours-btn" className="w-full py-3 border border-[#262626] hover:border-[#16A34A]/30 hover:bg-[#16A34A]/5 text-white font-semibold rounded-xl text-sm transition-all duration-200 font-heading">
                    Book Happy Hours
                  </button>
                </div>

                {/* Rush Hours */}
                <div className="p-6 md:p-8" data-testid="pricing-card-rush-hours">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                      <Zap className="h-4 w-4 text-[#16A34A]" />
                      <div>
                        <h3 className="text-[15px] font-bold font-heading">Rush Hours</h3>
                        <span className="text-[10px] text-neutral-500 font-body">3 PM — 3 AM</span>
                      </div>
                    </div>
                    <span className="text-[9px] text-white bg-[#16A34A] px-2.5 py-0.5 rounded-full font-bold font-heading uppercase tracking-wider">Popular</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-[#0A0A0A] border border-[#16A34A]/10 rounded-xl p-3.5 text-center" data-testid="weekday-price-rush">
                      <div className="text-[9px] text-neutral-600 uppercase tracking-wider mb-1 font-body">Mon — Fri</div>
                      <div className="flex items-baseline justify-center gap-0.5">
                        <span className="text-2xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹400</span>
                        <span className="text-[10px] text-neutral-600 font-body">/hr</span>
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#16A34A]/10 rounded-xl p-3.5 text-center" data-testid="weekend-price-rush">
                      <div className="text-[9px] text-neutral-600 uppercase tracking-wider mb-1 font-body">Sat — Sun</div>
                      <div className="flex items-baseline justify-center gap-0.5">
                        <span className="text-2xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹500</span>
                        <span className="text-[10px] text-neutral-600 font-body">/hr</span>
                      </div>
                    </div>
                  </div>

                  <button onClick={() => navigate('/login')} data-testid="book-rush-hours-btn" className="w-full py-3 bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl text-sm transition-colors font-heading">
                    Book Rush Hours
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Good to Know ─── */}
      <section className="pb-16 md:pb-20 pt-2 px-6 md:px-10 lg:px-16" data-testid="good-to-know-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5 md:p-6">
            <div className="flex items-center gap-2.5 mb-4">
              <Info className="h-4 w-4 text-[#16A34A]" />
              <h3 className="text-sm font-bold font-heading">Good to Know</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">Cricket</div>
                <p className="text-[12px] text-neutral-400 leading-relaxed font-body">We do not provide bat or ball. You can purchase cricket turf balls from us at the venue.</p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">Football</div>
                <p className="text-[12px] text-neutral-400 leading-relaxed font-body">Football is provided at the turf. Just bring your team and your game!</p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">Beverages</div>
                <p className="text-[12px] text-neutral-400 leading-relaxed font-body">Drinks and refreshments are available for purchase at the venue.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Gallery — Happy Moments ─── */}
      <GallerySection images={galleryImages} />

      {/* ─── Testimonials ─── */}
      <section id="reviews" className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#16A34A] text-[11px] font-semibold tracking-[0.2em] uppercase font-heading">Reviews</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-3 font-heading">What Players Say</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 hover:border-[#262626] transition-colors" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`h-3.5 w-3.5 ${j < t.rating ? 'text-[#16A34A] fill-[#16A34A]' : 'text-neutral-800'}`} />
                  ))}
                </div>
                <p className="text-neutral-400 text-[13px] leading-relaxed mb-5 font-body">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#1A1A1A]">
                  <div className="w-9 h-9 rounded-full bg-[#16A34A]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#16A34A] text-xs font-bold font-heading">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold font-heading">{t.name}</div>
                    <div className="text-[11px] text-neutral-600 font-body">Verified Player</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Ready to Play CTA ─── */}
      <section className="relative overflow-hidden" data-testid="ready-to-play-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/2413089/pexels-photo-2413089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }} />
        <div className="absolute inset-0 bg-black/85" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-heading">
              Ready to <span className="text-[#16A34A]">Play?</span>
            </h2>
            <p className="text-neutral-400 mb-8 font-body">
              Book your slot in seconds. Get instant confirmation. No hassle, no waiting.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => navigate('/login')} data-testid="cta-book-now-button" className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold px-8 py-4 rounded-xl text-sm flex items-center gap-2 transition-colors font-heading">
                Book Your Slot <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => scrollTo('contact')} className="bg-white/10 backdrop-blur-sm hover:bg-white/15 text-white font-semibold px-8 py-4 rounded-xl text-sm flex items-center gap-2 transition-colors font-heading">
                <Phone className="h-4 w-4" /> Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Contact ─── */}
      <section id="contact" className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left - Info & Map */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <span className="text-[#16A34A] text-[11px] font-semibold tracking-[0.2em] uppercase font-heading">Contact</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 mb-3 font-heading">Get in Touch</h2>
                <p className="text-sm text-neutral-500 font-body max-w-md">Have questions about booking or want to organise a tournament? Reach out to us.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href="https://maps.google.com/?q=Malda+Turf" target="_blank" rel="noopener noreferrer" className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                  <MapPin className="h-5 w-5 text-[#16A34A] mb-2" />
                  <div className="text-[11px] text-neutral-600 font-body mb-0.5">Location</div>
                  <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">Malda, West Bengal</div>
                </a>
                <a href="tel:+918918695356" className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                  <Phone className="h-5 w-5 text-[#16A34A] mb-2" />
                  <div className="text-[11px] text-neutral-600 font-body mb-0.5">Phone</div>
                  <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">+91 89186 95356</div>
                </a>
                <a href="mailto:maldaturf@gmail.com" className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                  <Mail className="h-5 w-5 text-[#16A34A] mb-2" />
                  <div className="text-[11px] text-neutral-600 font-body mb-0.5">Email</div>
                  <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">maldaturf@gmail.com</div>
                </a>
              </div>

              <div className="rounded-2xl overflow-hidden border border-[#1A1A1A]" style={{ height: '240px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3616.0311586194234!2d88.10644167598473!3d24.999057177839173!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39fafdb3f6830bd1%3A0xc165b489532e7178!2sMalda%20Turf%20cricket%20and%20football!5e0!3m2!1sen!2sin!4v1776782074975!5m2!1sen!2sin"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Malda Turf Location"
                />
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleContactSubmit} className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-7 space-y-4 h-full flex flex-col" data-testid="contact-form">
                <h3 className="text-base font-bold font-heading mb-1">Send a Message</h3>
                <input type="text" placeholder="Your Name" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} required className="rounded-xl text-sm" data-testid="contact-name-input" />
                <input type="email" placeholder="Email Address" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} required className="rounded-xl text-sm" data-testid="contact-email-input" />
                <input type="tel" placeholder="Phone Number" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full rounded-xl text-sm" data-testid="contact-phone-input" />
                <textarea placeholder="Your Message" rows={4} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} required className="w-full resize-none rounded-xl text-sm flex-1" data-testid="contact-message-input" />
                {contactSubmitted && (
                  <div className="flex items-center gap-2 bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-xl p-3" data-testid="contact-success-message">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0" />
                    <span className="text-[12px] text-[#16A34A] font-medium font-body">Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}
                <button type="submit" disabled={contactSending} data-testid="contact-submit-button" className="w-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl text-sm transition-colors font-heading mt-auto">
                  {contactSending ? 'Sending...' : contactSubmitted ? 'Message Sent!' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Imagination Partner ─── */}
      <section className="relative py-10 md:py-12 px-6 md:px-10 lg:px-16 bg-[#0A0A0A] border-t border-[#1A1A1A] overflow-hidden" data-testid="partner-section">
        <div className="absolute inset-0 bg-[#16A34A]/[0.02]" />
        <div className="max-w-[1440px] mx-auto relative z-10">
          <div className="flex items-center justify-center gap-3">
            <div className="h-[1px] flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-[#1A1A1A]"></div>
            <div className="flex items-center gap-2.5">
              <span className="text-[10px] text-neutral-600 uppercase tracking-[0.15em] font-body">Imagination Partner</span>
              <span className="text-[10px] text-neutral-700">|</span>
              <span className="text-[13px] font-bold font-heading text-neutral-300">Web Int Projects</span>
            </div>
            <div className="h-[1px] flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-[#1A1A1A]"></div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-[#1A1A1A] bg-[#111111]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-[#16A34A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50px' }}>
                  <Trophy className="h-4 w-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <span className="text-sm font-bold font-heading block">Malda Turf</span>
                  <span className="text-[10px] text-neutral-600 font-body">Premium Sports</span>
                </div>
              </div>
              <p className="text-[12px] text-neutral-500 leading-relaxed font-body">Professional cricket & football turf in Malda, West Bengal. Book online, play anytime.</p>
              {/* Social icons — mobile only (icons-only) */}
              <div className="flex items-center gap-2 mt-4 md:hidden">
                {[
                  { label: 'YouTube', href: 'https://youtube.com/@maldaturf', d: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
                  { label: 'Facebook', href: 'https://facebook.com/maldaturf', d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                  { label: 'Instagram', href: 'https://instagram.com/maldaturf', d: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] hover:border-[#262626] flex items-center justify-center transition-colors" aria-label={s.label} data-testid={`footer-social-mobile-${s.label.toLowerCase()}`}>
                    <svg className="w-3.5 h-3.5 text-neutral-500" fill="currentColor" viewBox="0 0 24 24"><path d={s.d} /></svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links — side by side with Policies on mobile */}
            <div>
              <h4 className="text-[12px] font-semibold text-neutral-400 uppercase tracking-wider mb-4 font-heading">Quick Links</h4>
              <div className="space-y-2.5">
                {[{ label: 'Pricing', id: 'pricing' }, { label: 'Gallery', id: 'gallery' }, { label: 'Reviews', id: 'reviews' }, { label: 'Contact', id: 'contact' }].map(item => (
                  <button key={item.id} onClick={() => scrollTo(item.id)} className="block text-[12px] text-neutral-500 hover:text-white transition-colors font-body" data-testid={`footer-link-${item.id}`}>{item.label}</button>
                ))}
              </div>
            </div>

            {/* Policies — side by side with Quick Links on mobile */}
            <div>
              <h4 className="text-[12px] font-semibold text-neutral-400 uppercase tracking-wider mb-4 font-heading">Policies</h4>
              <div className="space-y-2.5">
                {[{ label: 'Terms & Conditions', path: '/terms' }, { label: 'Privacy Policy', path: '/privacy' }, { label: 'Refund Policy', path: '/refund' }, { label: 'Disclaimer', path: '/disclaimer' }].map(item => (
                  <button key={item.path} onClick={() => navigate(item.path)} className="block text-[12px] text-neutral-500 hover:text-white transition-colors font-body" data-testid={`footer-policy-${item.path.slice(1)}`}>{item.label}</button>
                ))}
              </div>
            </div>

            {/* Social Links — desktop only (with labels) */}
            <div className="hidden md:block">
              <h4 className="text-[12px] font-semibold text-neutral-400 uppercase tracking-wider mb-4 font-heading">Follow Us</h4>
              <div className="space-y-2.5">
                {[
                  { label: 'YouTube', href: 'https://youtube.com/@maldaturf', d: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
                  { label: 'Facebook', href: 'https://facebook.com/maldaturf', d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                  { label: 'Instagram', href: 'https://instagram.com/maldaturf', d: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-neutral-500 hover:text-white transition-colors group" data-testid={`footer-social-${s.label.toLowerCase()}`}>
                    <div className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] group-hover:border-[#262626] flex items-center justify-center transition-colors">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d={s.d} /></svg>
                    </div>
                    <span className="text-[12px] font-body">{s.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-[#1A1A1A] flex flex-col md:flex-row items-center justify-between gap-3">
            <span className="text-[11px] text-neutral-700 font-body">{'\u00A9'} 2026 Malda Turf. All rights reserved.</span>
            <div className="flex items-center gap-4 text-[11px] text-neutral-700 font-body">
              <span>Malda, West Bengal, India</span>
              <span>&middot;</span>
              <span>Open 24/7</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
